import { useShadow, useShadowStyle } from "../../util/wcUtil.ts"
import { isSaveTrackable } from "../../features/host/fileSave.ts"
import '../base/as-spinner.ts';
import styleText from './shared-modal-styles.css?raw';

/**
 * Unsaved-changes exit warning, shared by every "leaving with unsaved changes"
 * flow: the home button, new-session, mesh transfer, and the window-close
 * (beforeunload) guard.
 *
 * Three phases, driven by attributes:
 *   - Waiting (`waiting`): a "finishing up" spinner shown while an in-flight
 *     configure/export completes before the close proceeds. No buttons — the
 *     close is already committed and just waiting for the app to go idle.
 *   - Phase 1 (default): [Cancel] [<verb> without saving] [Save]
 *   - Phase 2 (`saved`):  [Cancel] [<verb>]
 * Clicking Save runs the regular file save then advances to Phase 2, where the
 * user decides when to actually leave — so we never have to detect when an
 * untrackable browser download finished before navigating away.
 *
 * `mode` selects the verb: 'close' -> Close, 'home' -> Exit, 'new' -> Start new,
 * 'transfer' -> Transfer.
 * Button names map to ExitMixin's handler:
 *   as-intrn-cancel-exit-app | as-intrn-confirm-exit-app (proceed/discard) | as-intrn-save-before-exit
 * The `as-intrn-` prefix marks these as internal to as-app: ExitMixin already
 * owns and consumes them (see _handleExitButtonClick), translating the relevant
 * one into the host-facing 'save-and-close' event. A host's own
 * as-button-click switch should never add a case for an as-intrn- name; those
 * are already spoken for.
 *
 * The Save button label depends on whether the environment's save provider is
 * trackable (see fileSave.ts `isSaveTrackable`): an untrackable browser
 * <a download> can't report when it finished, so Save just advances to phase 2
 * and the label stays a plain "Save". A trackable provider (GrabCAD's native
 * dialog) finishes in one step, so the label folds the exit verb in —
 * "Save and Exit" / "Save and Close" / "Save and Start new".
 */

/**
 * Button text per mode. A key per mode rather than one sentence with the verb
 * dropped into it: word order and case differ by language.
 */
const MODE_TEXT: Record<string, { verb: string; discard: string; saveAnd: string }> = {
  close:    { verb: 'Close',     discard: 'Close without saving',     saveAnd: 'Save and Close' },
  home:     { verb: 'Exit',      discard: 'Exit without saving',      saveAnd: 'Save and Exit' },
  new:      { verb: 'Start new', discard: 'Start new without saving', saveAnd: 'Save and Start new' },
  transfer: { verb: 'Transfer',  discard: 'Transfer without saving',  saveAnd: 'Save and Transfer' },
};

export class AsModalUnsavedWarning extends HTMLElement {
  static readonly tag = 'as-modal-unsaved-warning' as const;

  static get observedAttributes() {
    return ['mode', 'saved', 'waiting'];
  }

  get verb(): string {
    return MODE_TEXT[this.modeKey].verb;
  }

  /** The MODE_TEXT row in use, and the suffix on every per-mode dict key. */
  get modeKey(): string {
    const mode = this.getAttribute('mode') ?? '';
    return mode in MODE_TEXT ? mode : 'home';
  }

  /** Phase-1 "leave without saving" label. */
  get discardLabel(): string {
    return MODE_TEXT[this.modeKey].discard;
  }

  /** Save button key, matching saveLabel's two shapes. */
  get saveDictKey(): string {
    return isSaveTrackable() ? `modals.unsaved.saveAnd.${this.modeKey}` : 'modals.unsaved.save';
  }

  /**
   * Phase-1 Save button label. Trackable providers (the current environment's
   * save-status reporting — see fileSave.ts isSaveTrackable) complete the
   * save-and-exit in one click, so the verb is folded in ("Save and Exit").
   * See exit-mixin.ts 'as-intrn-save-before-exit'. Untrackable saves advance
   * to a separate phase-2 confirm instead, so the label stays a plain "Save".
   */
  get saveLabel(): string {
    return isSaveTrackable() ? MODE_TEXT[this.modeKey].saveAnd : 'Save';
  }

  get html() {
    const verb = this.verb;

    if (this.hasAttribute('waiting')) {
      // Waiting — the close is committed and we're holding it until an in-flight
      // configure/export finishes. No buttons: there's nothing to decide yet.
      return /*html*/`
        <div id="warning-panel" class="panel-content">
          <div class="row option-buttons">
            <as-spinner style="--spinner-width: 2rem;"></as-spinner>
          </div>
          <as-text dict-key="apps.general.finishing-up" class="center">Finishing up, please wait...
          </as-text>
        </div>
      `;
    }

    if (this.hasAttribute('saved')) {
      // Phase 2 — work has been saved (or a browser download was triggered); the
      // user chooses when to leave.
      return /*html*/`
        <div id="warning-panel" class="panel-content">
          <as-text dict-key="apps.general.saved-leave-when-ready">Your changes have been saved. You can leave when you are ready.
          </as-text>
          <div class="row option-buttons">
            <as-text-button name="as-intrn-cancel-exit-app" dict-key="modals.unsaved.cancel">Cancel</as-text-button>
            <as-text-button name="as-intrn-confirm-exit-app" dict-key="modals.unsaved.proceed.${this.modeKey}">${verb}</as-text-button>
          </div>
        </div>
      `;
    }

    // Phase 1 — unsaved.
    return /*html*/`
        <div id="warning-panel" class="panel-content warning">
          <as-text dict-key="apps.general.may-lose-changes">You have unsaved changes.
          </as-text>
          <as-text dict-key="apps.general.save-before-leaving" class="center">Save your work before leaving?
          </as-text>
          <div class="row option-buttons">
            <as-text-button name="as-intrn-cancel-exit-app" dict-key="modals.unsaved.cancel">Cancel</as-text-button>
            <as-text-button name="as-intrn-confirm-exit-app" dict-key="modals.unsaved.discard.${this.modeKey}">${this.discardLabel}</as-text-button>
            <as-text-button name="as-intrn-save-before-exit" dict-key="${this.saveDictKey}">${this.saveLabel}</as-text-button>
          </div>
        </div>
    `
  }

  connectedCallback() {
    useShadow(this, this.html)
    useShadowStyle(this, styleText, './shared-modal-styles.css')
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue !== newValue && this.shadowRoot) {
      this.shadowRoot.innerHTML = this.html;
      useShadowStyle(this, styleText, './shared-modal-styles.css')
    }
  }
}

customElements.define(AsModalUnsavedWarning.tag, AsModalUnsavedWarning)
